import React, { useEffect, useMemo, useState } from 'react'
import { Box, MenuItem, Select, Typography } from '@mui/material'
import IntelLayout from './components/IntelLayout'
import KpiCard from './components/KpiCard'
import Badge from './components/Badge'
import IntelTable from './components/IntelTable'
import { intelApi } from './api'
import { intelColors, formatNumber } from './components/intelTheme'

const categoryColor = (c) => {
  switch (c) {
    case 'FORWARD':
      return 'blue'
    case 'REVERSE':
      return 'orange'
    case 'HIGH_VALUE':
      return 'purple'
    case 'SDD_NDD':
      return 'green'
    case 'HYPERLOCAL':
      return 'grey'
    default:
      return 'grey'
  }
}

const CATEGORY_LABEL = { FORWARD: 'Forward', REVERSE: 'Reverse', HIGH_VALUE: 'High Value', SDD_NDD: 'SDD / NDD', HYPERLOCAL: 'Hyperlocal' }
const CATEGORY_ORDER = ['FORWARD', 'REVERSE', 'HIGH_VALUE', 'SDD_NDD', 'HYPERLOCAL']

// Payment Mode = modes the ST's mapped active couriers collectively support (isCod + isPrepaid).
const payModeBadge = (m) => {
  const map = { BOTH: { color: 'green', label: 'Both' }, COD: { color: 'orange', label: 'COD' }, PREPAID: { color: 'blue', label: 'Prepaid' } }
  const cfg = map[m] || { color: 'grey', label: '—' }
  return <Badge color={cfg.color}>{cfg.label}</Badge>
}

export default function ServiceTypeTaxonomy() {
  const [category, setCategory] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    intelApi
      .serviceTypes({ category, includeLinkedCodes: true })
      .then((res) => !cancel && setData(res?.data?.responseObj || res?.data?.data || res?.data))
      .catch((e) => !cancel && setErr(e?.response?.data?.exception || e.message))
      .finally(() => !cancel && setLoading(false))
    return () => {
      cancel = true
    }
  }, [category])

  const sts = data?.serviceTypes || []
  const byCat = data?.byCategory || {}

  const columns = useMemo(
    () => [
      { key: 'code', header: 'ST Code', render: (r) => <code style={{ background: '#f1f3f5', padding: '1px 5px', borderRadius: 3 }}>{r.code}</code> },
      { key: 'name', header: 'Name', render: (r) => <strong>{r.name}</strong> },
      { key: 'cat', header: 'Category', render: (r) => <Badge color={categoryColor(r.category)}>{CATEGORY_LABEL[r.category] || r.category}</Badge> },
      { key: 'dir', header: 'Direction', render: (r) => <Badge color={r.direction === 'RVP' ? 'orange' : 'blue'}>{r.direction === 'RVP' ? 'Reverse' : r.direction === 'FORWARD' ? 'Forward' : r.direction || '—'}</Badge> },
      { key: 'mode', header: 'Mode', render: (r) => <Badge color={r.mode === 'AIR' ? 'purple' : r.mode === 'HYPERLOCAL' ? 'grey' : 'blue'}>{r.mode}</Badge> },
      { key: 'wb', header: 'Weight Band', render: (r) => r.weightBand || '—' },
      { key: 'wt', header: 'Weight Range', render: (r) => <span style={{ fontSize: 11 }}>{r.weightRangeLabel || '—'}</span> },
      { key: 'sla', header: 'SLA', render: (r) => <span style={{ fontSize: 11 }}>{r.slaDescription}</span> },
      { key: 'pay', header: 'Payment Mode', render: (r) => payModeBadge(r.paymentMode) },
      { key: 'qc', header: 'QC', render: (r) => (r.requiresQc ? <Badge color='blue'>Yes</Badge> : '—') },
      { key: 'ins', header: 'Insurance', render: (r) => (r.requiresInsurance ? <Badge color='purple'>Req</Badge> : '—') },
      { key: 'otp', header: 'OTP', render: (r) => (r.requiresOtp ? <Badge color='blue'>Yes</Badge> : '—') },
      { key: 'codes', header: 'Linked Codes', render: (r) => <span style={{ fontSize: 11, color: '#555' }}>{formatNumber(r.totalLinkedCount)} {r.exampleAccountCodes?.length ? `(${r.exampleAccountCodes.slice(0, 3).join(', ')}…)` : ''}</span> },
      { key: 'pickup', header: 'Pickup Pincodes', render: (r) => formatNumber(r.pickupPinCount) },
      { key: 'deliv', header: 'Delivery Pincodes', render: (r) => formatNumber(r.deliveryPinCount) },
      { key: 'base', header: 'Routes Via', render: (r) => (r.baseServiceType ? <code style={{ fontSize: 11 }}>{r.baseServiceType}</code> : '—') },
      { key: 'active', header: 'Active', render: (r) => (r.active ? <Badge color='green'>Yes</Badge> : <Badge color='grey'>No</Badge>) },
    ],
    []
  )

  return (
    <IntelLayout title='Service Type Taxonomy'>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '14px', mb: 2 }}>
        <KpiCard loading={loading} color='blue' label='Total Service Types' value={formatNumber(data?.totalCount)} />
        {[...CATEGORY_ORDER.filter((c) => c in byCat), ...Object.keys(byCat).filter((c) => !CATEGORY_ORDER.includes(c))].map((cat) => (
          <KpiCard loading={loading}
            key={cat}
            color={categoryColor(cat)}
            label={CATEGORY_LABEL[cat] || cat}
            value={formatNumber(byCat[cat])}
          />
        ))}
      </Box>

      <Box sx={{ background: '#fff', border: `1px solid ${intelColors.border}`, borderRadius: '8px', padding: '12px 16px', mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase' }}>Filter by Category</Typography>
        <Select size='small' value={category} onChange={(e) => setCategory(e.target.value)} displayEmpty sx={{ width: 220, fontSize: 12, '& .MuiSelect-select': { padding: '7px 10px' } }}>
          <MenuItem value=''>All</MenuItem>
          {CATEGORY_ORDER.map((c) => <MenuItem key={c} value={c}>{CATEGORY_LABEL[c]}</MenuItem>)}
        </Select>
      </Box>

      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>
          {err}
        </Box>
      )}

      <IntelTable title='Service Type Catalog' loading={loading} columns={columns} rows={sts} rowKey={(r) => r.code} pageSize={50} />
    </IntelLayout>
  )
}
